/**
 * Channels Invariants
 * 
 * Permission control, normalization, and validation.
 */

import { CommandOperationMap, Invariants, gateCommandByOperation } from '@commandDrivenStateEngine/core/invariants';
import { ChannelsState } from './state';
import { ChannelsCommand } from './commands';
import {
  isCategoryCommand,
  canApplyCategoryCommand,
  normalizeCategories
} from './categoriesInvariants';

/**
 * Command operations — each user-sourced loading flip triggers a policy that
 * calls the backend. Capability is DERIVED from the named client method:
 * - `loading/setSubmitting` → ProvisionSystemIntegration ( add-channel connect ).
 * - `detail/loading/setSaving{Billing,Defaults,RetailStoreConfig}` →
 *   UpdateSystemIntegrationConfiguration ( channel-detail config saves ).
 * - `detail/loading/setImporting` → StartChannelPublicationImportBatchProcess.
 * The OAuth completion path also provisions, but is api/policy-driven ( not a
 * user command ) so it is authorized upstream.
 */
export const commandOperations: CommandOperationMap<ChannelsState, ChannelsCommand> = {
  'integrations/channels/loading/setSubmitting': 'ProvisionSystemIntegration',
  'integrations/channels/detail/loading/setSavingBilling': 'UpdateSystemIntegrationConfiguration',
  'integrations/channels/detail/loading/setSavingDefaults': 'UpdateSystemIntegrationConfiguration',
  'integrations/channels/detail/loading/setSavingRetailStoreConfig': 'UpdateSystemIntegrationConfiguration',
  'integrations/channels/detail/statusMapping/save': 'UpdateSystemIntegrationConfiguration',
  'integrations/channels/detail/loading/setImporting': 'StartChannelPublicationImportBatchProcess'
};

export const invariants: Invariants<ChannelsState, ChannelsCommand> = {
  /**
   * Permission control based on source
   */
  canApply: ({ state, command, meta }) => {
    const { type } = command;
    const { source } = meta;

    // Category commands have their own dedicated permission matrix
    // ( user + url for map/unmap/search, api for data/results/loading ).
    if ( isCategoryCommand( command )) {
      return canApplyCategoryCommand({ command, meta });
    }

    // API source: data sync, loading states, modal closes
    if ( source === 'api' || source === 'policy' ) {
      return (
        type.startsWith( 'integrations/channels/data/' ) ||
        type.startsWith( 'integrations/channels/loading/' ) ||
        type.startsWith( 'integrations/channels/detail/loading/' ) ||
        type.startsWith( 'integrations/channels/detail/billingEntities/' ) ||
        type.startsWith( 'integrations/channels/detail/warehouses/' ) ||
        type.startsWith( 'integrations/channels/detail/priceLists/' ) ||
        type === 'integrations/channels/detail/statusMapping/load' ||
        type === 'integrations/channels/addModal/close' ||
        type === 'integrations/channels/delete/close' ||
        type === 'integrations/channels/detail/close' ||
        type.startsWith( 'integrations/channels/detail/defaultValues/setConfig' ) ||
        type.startsWith( 'integrations/channels/detail/defaultValues/saveSuccess' ) ||
        type.startsWith( 'integrations/channels/detail/defaultValues/clearSuccess' ) ||
        type === 'integrations/channels/detail/billingEntity/cancel' ||
        type === 'integrations/channels/detail/retailStoreConfig/cancel' ||
        /*
         * Per-channel figures for the LIST: publication counts, category
         * coverage and setup coverage (TV1-2361 / TV1-2367).
         *
         * This is read-only derived data loaded by `channelStatsPolicy`, so
         * `api` is the ONLY source that should ever write it — no user handler
         * dispatches it.
         *
         * Its omission is why the cards showed "—" for both figures and hung on
         * "Verificando configuración…": all three loaders queried successfully
         * and all three dispatches were silently dropped by this matrix. The
         * only trace was a console warning, so from the UI it was
         * indistinguishable from a failing query — and it cost several rounds of
         * diagnosis aimed at the queries instead. A blocked command is a no-op,
         * not an error; adding a command to a policy means adding it here too.
         */
        type === 'integrations/channels/stats/set' ||
        type.startsWith( 'integrations/channels/oauth/' )
      );
    }

    // AI source: can browse and fill forms, but NOT submit or delete
    if ( source === 'ai' ) {
      // Block destructive actions
      if (
        type === 'integrations/channels/addModal/submit' ||
        type === 'integrations/channels/delete/confirm' ||
        type === 'integrations/channels/detail/billingEntity/save' ||
        type === 'integrations/channels/detail/defaultValues/save' ||
        type === 'integrations/channels/detail/delete' ||
        type === 'integrations/channels/detail/importPublications' ||
        // TV1-2354 — an agent must not arm an automatic channel-wide import; the
        // intent is a human decision taken at connect time, same class as the
        // manual import above. TV1-2379 widened the payload to carry WHICH kind
        // (`publications` | `orders`); the block covers both, and the order kind
        // is the one that most needs it — it pulls the seller's entire past
        // order history.
        type === 'integrations/channels/autoImport/setPending' ||
        type === 'integrations/channels/detail/retailStoreConfig/save' ||
        type === 'integrations/channels/detail/statusMapping/save'
      ) {
        return false;
      }

      // Block direct data mutations
      if ( type.startsWith( 'integrations/channels/data/' )) {
        return false;
      }

      /*
       * Derived read-only figures (TV1-2367). Same class as `data/` above: the
       * `ai` branch is allow-by-default, so anything the assistant must not
       * author has to be named here. These figures are what the cards report a
       * channel's health from — publication counts, category coverage, and
       * whether setup is actually done — so an agent able to write them could
       * make an unconfigured channel read as ready.
       */
      if ( type === 'integrations/channels/stats/set' ) {
        return false;
      }

      return true;
    }

    // URL source: only navigation-related. `detail/setTab` is included so the
    // active tab can be restored from the `?tab=` query-param on a page refresh
    // (otherwise the tab — and its lazily-loaded data, e.g. categories — is lost).
    if ( source === 'url' ) {
      return (
        type === 'integrations/channels/detail/open' ||
        type === 'integrations/channels/detail/setTab'
      );
    }

    // User source: everything except API commands; privileged provision /
    // configure / import triggers gated by the backend operation in
    // commandOperations.
    if ( source === 'user' ) {
      if ( type.startsWith( 'integrations/channels/data/' )) return false;
      /*
       * Derived read-only figures (TV1-2367) — loaded by `channelStatsPolicy`
       * and never authored by a UI handler. Refused here for the same reason as
       * `data/`: `gateCommandByOperation` allows anything with no operation
       * mapped, so a command that no user should ever send has to say so.
       */
      if ( type === 'integrations/channels/stats/set' ) return false;
      return gateCommandByOperation( commandOperations, { state, command, meta });
    }

    return false;
  },

  /**
   * State normalization
   */
  normalize: ( state ) => {
    let normalized = { ...state };

    // If detail view is for a non-existent channel, close it
    if (
      normalized.channelDetail.mode.type === 'view' &&
      !normalized.salesChannels[normalized.channelDetail.mode.integrationId]
    ) {
      normalized = {
        ...normalized,
        channelDetail: {
          ...normalized.channelDetail,
          mode: { type: 'closed' }
        }
      };
    }

    // Functional-map normalization: collapse duplicate category rows so a
    // single externalId never has two targets client-side.
    normalized = normalizeCategories( normalized );

    return normalized;
  },

  /**
   * Validation for UI feedback
   */
  validate: ( state ) => {
    // Only validate when add modal is in enterDetails step
    if ( state.addChannelModal.isOpen && state.addChannelModal.step === 'enterDetails' ) {
      const errors: Record<string, string> = {};
      const { selectedType, formData } = state.addChannelModal;

      // Validate based on channel type
      if ( selectedType === 'woocommerce' ) {
        if ( !formData.storeUrl.trim()) {
          errors.storeUrl = 'Store URL is required';
        }
        if ( !formData.consumerKey.trim()) {
          errors.consumerKey = 'Consumer Key is required';
        }
        if ( !formData.consumerSecret.trim()) {
          errors.consumerSecret = 'Consumer Secret is required';
        }
      } else if ( selectedType === 'amazon' ) {
        if ( !formData.sellerId.trim()) {
          errors.sellerId = 'Seller ID is required';
        }
      } else if ( selectedType === 'retailstore' ) {
        if ( !formData.name.trim()) {
          errors.name = 'Store name is required';
        }
      } else if ([ 'tiendanube', 'shopify', 'fenicio' ].includes( selectedType || '' )) {
        if ( !formData.storeUrl.trim()) {
          errors.storeUrl = 'Store URL is required';
        }
      }

      return Object.keys( errors ).length > 0 ? errors : null;
    }

    return null;
  }
};
